import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { enquiriesApi } from "@/lib/enquiries";
import { ClientPicker, type ClientPickerValue } from "@/components/client-picker";
import { PageContainer } from "@/app/layouts/PageContainer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";

export function NewEnquiryPage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [client, setClient] = useState<ClientPickerValue | null>(null);
  const [description, setDescription] = useState("");

  const create = useMutation({
    mutationFn: () =>
      enquiriesApi.create({
        clientId: client?.clientId ?? null,
        description: description || null,
      }),
    onSuccess: (e) => {
      void qc.invalidateQueries({ queryKey: ["enquiries"] });
      toast.success(`Enquiry #${e.enquiryId} created.`);
      nav(`/enquiries/${e.enquiryId}`);
    },
    onError: (err: Error) => toast.error(`Create failed: ${err.message}`),
  });

  return (
    <PageContainer>
      <h1 className="text-2xl font-semibold mb-4">New Enquiry</h1>

      <div className="space-y-4 max-w-lg">
        <div>
          <Label>Client</Label>
          <ClientPicker value={client} onChange={setClient} />
        </div>
        <div>
          <Label htmlFor="new-enquiry-description">Description</Label>
          <Input
            id="new-enquiry-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Brief description of the enquiry…"
            aria-label="Description"
          />
        </div>
      </div>

      <div className="mt-8 flex gap-3">
        <Button variant="outline" onClick={() => nav("/enquiries")}>
          Cancel
        </Button>
        <Button
          onClick={() => create.mutate()}
          disabled={create.isPending}
          aria-label="Create Enquiry"
        >
          {create.isPending ? "Creating…" : "Create Enquiry"}
        </Button>
      </div>
    </PageContainer>
  );
}
